import type { QueryClient } from "@tanstack/react-query";

/**
 * Refresh every cached view of PV sessions after a mutation touches one:
 * the builder (`pvSession`), the sessions list, the draft pickers and the
 * active-draft lookups. Prefix keys, so every league/season filter refreshes.
 *
 * (The historical `["disciplinarySessions"]` key has no reader — it
 * invalidated nothing, leaving case counts in lists and pickers stale.)
 * The FAF report check (`pvReportCheck`) reads the same sanctions and cases,
 * and its card lines come from the FAF sanction audit (`fafSanctionAudit`,
 * 5-minute staleTime): a card sanction added in the builder must clear them.
 * The bell's Discipline items (`["pvReportCheck", "pending", …]`) follow the report check.
 */
export function invalidatePVSessionQueries(qc: QueryClient, sessionId?: string | null): void {
    if (sessionId) {
        qc.invalidateQueries({ queryKey: ["pvSession", sessionId] });
        qc.invalidateQueries({ queryKey: ["pvReportCheck", sessionId] });
        qc.invalidateQueries({ queryKey: ["pvReportCheck", "pending"] });
    } else {
        qc.invalidateQueries({ queryKey: ["pvSession"] });
        qc.invalidateQueries({ queryKey: ["pvReportCheck"] });
    }
    qc.invalidateQueries({ queryKey: ["pvSessionsList"] });
    qc.invalidateQueries({ queryKey: ["draftPVSessions"] });
    qc.invalidateQueries({ queryKey: ["activePVSession"] });
    qc.invalidateQueries({ queryKey: ["fafSanctionAudit"] });
}

/**
 * A sanction write the server refused (PV published meanwhile, duplicate,
 * archived season…): nothing was written, but the UI decided from a PV state
 * that may be stale. Refresh what the « editable » decision reads — the PV
 * badges (`sanctionPvStatus`) and every PV view — never the audit cache
 * (mark_audit_cache_stale is a write, and nothing changed).
 */
export function invalidateSanctionPvStateQueries(qc: QueryClient, sessionId?: string | null): void {
    qc.invalidateQueries({ queryKey: ["sanctionPvStatus"] });
    invalidatePVSessionQueries(qc, sessionId);
}
